import { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { api, img, useApi, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SingleImage } from "@/pages/admin/ImageManager";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { SpanishFields, cleanEs } from "@/pages/admin/SpanishFields";

const ES_FIELDS = [{ key: "title", label: "Título", type: "input" }, { key: "cta_label", label: "Texto del botón", type: "input" }];
const BLANK = { image: "", title: "", cta_label: "", cta_url: "", order: 999, published: true, es: {} };
const FIELDS = [["title", "Heading"], ["cta_label", "Button text"], ["cta_url", "Button link (https://…)"]];

const BTN_ES = [{ key: "hero_cta1_label", label: "Texto del botón 1", type: "input" }, { key: "hero_cta2_label", label: "Texto del botón 2", type: "input" }];
const BTN_FIELDS = [["hero_cta1_label", "Button 1 text"], ["hero_cta1_url", "Button 1 link (/page or https://…)"], ["hero_cta2_label", "Button 2 text"], ["hero_cta2_url", "Button 2 link (/page or https://…)"]];

const WelcomeForm = ({ initial }) => {
  const [f, setF] = useState(initial);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", { ...f, slide_seconds: Number(f.slide_seconds), es: cleanEs(f.es) });
      toast.success("Welcome slide saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className="mb-10 space-y-5 rounded-2xl border border-clay bg-white p-6 sm:p-8" data-testid="welcome-form">
      <div>
        <h2 className="text-xl font-black text-forest">Welcome slide & timing</h2>
        <p className="mt-1 text-xs font-medium text-moss">The two buttons on the first slide. Leave a button's text or link empty to hide it. Heading and photo are in Site Settings.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {BTN_FIELDS.map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label className="font-bold">{label}</Label>
            <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`welcome-field-${k}`} />
          </div>
        ))}
        <div className="space-y-1.5">
          <Label className="font-bold">Seconds per slide (3–20)</Label>
          <Input type="number" min={3} max={20} required value={f.slide_seconds} onChange={(e) => setF({ ...f, slide_seconds: e.target.value })} data-testid="welcome-field-slide_seconds" />
        </div>
      </div>
      <SpanishFields fields={BTN_ES} source={f} value={f.es || {}} onChange={(es) => setF({ ...f, es })} testId="welcome-es" />
      <button type="submit" className="btn-primary" data-testid="welcome-save">Save welcome slide</button>
    </form>
  );
};

const WelcomeBox = () => {
  const { data } = useApi("/settings");
  return data ? <WelcomeForm initial={data} /> : null;
};

const SlideForm = ({ slide, onSaved }) => {
  const [f, setF] = useState(slide);
  const save = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Please upload an image");
    try {
      const body = { ...f, es: cleanEs(f.es) };
      if (f.id) await api.put(`/admin/slides/${f.id}`, body);
      else await api.post("/admin/slides", body);
      toast.success("Slide saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="slide-form">
      <SingleImage value={f.image} onChange={(v) => setF({ ...f, image: v })} testId="slide-image-upload" />
      {FIELDS.map(([k, label]) => (
        <div key={k} className="space-y-1.5">
          <Label className="font-bold">{label}</Label>
          <Input required={k === "title"} type={k === "cta_url" ? "url" : "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`slide-field-${k}`} />
        </div>
      ))}
      <p className="text-xs text-moss">Leave the button link empty to show the slide without a button.</p>
      <label className="flex items-center gap-3 font-bold"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="slide-field-published" /> Show on homepage</label>
      <SpanishFields fields={ES_FIELDS} source={f} value={f.es || {}} onChange={(es) => setF({ ...f, es })} testId="slide-es" />
      <button type="submit" className="btn-primary w-full" data-testid="slide-save">Save slide</button>
    </form>
  );
};

const SlideRow = ({ d, i, last, onMove, onEdit, onRemove, onToggle }) => (
  <div className="flex items-center gap-4 border-b border-clay p-4 last:border-b-0" data-testid={`admin-slide-row-${d.id}`}>
    <div className="flex flex-col">
      <button disabled={i === 0} onClick={() => onMove(i, -1)} className="p-1 text-moss disabled:opacity-30" aria-label="Move up" data-testid={`slide-up-${d.id}`}><ArrowUp className="h-4 w-4" /></button>
      <button disabled={last} onClick={() => onMove(i, 1)} className="p-1 text-moss disabled:opacity-30" aria-label="Move down" data-testid={`slide-down-${d.id}`}><ArrowDown className="h-4 w-4" /></button>
    </div>
    <img src={img(d.image)} alt="" className={`h-16 w-24 shrink-0 rounded-lg object-cover ${d.published ? "" : "opacity-40"}`} />
    <div className="min-w-0 flex-1">
      <p className="truncate font-bold text-ink" data-testid={`slide-title-${d.id}`}>{d.title}</p>
      <p className="truncate text-sm font-semibold text-sage">{d.cta_url ? `${d.cta_label || "Button"} → ${d.cta_url}` : "No button"}</p>
    </div>
    <Switch checked={d.published} onCheckedChange={() => onToggle(d)} aria-label="Show on homepage" data-testid={`slide-toggle-${d.id}`} />
    <button onClick={() => onEdit(d)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-forest" aria-label="Edit" data-testid={`slide-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
    <button onClick={() => onRemove(d)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-destructive" aria-label="Delete" data-testid={`slide-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
  </div>
);

export default function AdminSlides() {
  const { data, reload, setData } = useApi("/admin/slides", []);
  const [editing, setEditing] = useState(null);
  const run = async (fn) => {
    try { await fn(); } catch (e) { toast.error(formatApiError(e)); }
    reload();
  };
  const move = (i, d) => {
    const next = [...data];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setData(next);
    run(() => api.post("/admin/slides-order", { ids: next.map((x) => x.id) }));
  };
  const toggle = (s) => run(() => api.put(`/admin/slides/${s.id}`, { ...s, published: !s.published }));
  const remove = (s) => window.confirm(`Delete slide "${s.title}"?`) && run(async () => { await api.delete(`/admin/slides/${s.id}`); toast.success("Deleted"); });
  return (
    <div data-testid="admin-slides">
      <AdminHeader title="Homepage Slider">
        <button onClick={() => setEditing({ ...BLANK, order: data.length })} className="btn-primary" data-testid="slide-new"><Plus className="h-4 w-4" /> New slide</button>
      </AdminHeader>
      <WelcomeBox />
      <p className="mb-4 text-sm text-moss">These slides play after the welcome slide. Use the arrows to change the order and the switch to hide a slide.</p>
      <div className="overflow-hidden rounded-2xl border border-clay bg-white" data-testid="slides-list">
        {data.map((d, i) => <SlideRow key={d.id} d={d} i={i} last={i === data.length - 1} onMove={move} onEdit={setEditing} onRemove={remove} onToggle={toggle} />)}
        {!data.length && <p className="p-6 text-sm text-moss" data-testid="slides-empty">No slides yet.</p>}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black text-forest">{editing?.id ? "Edit slide" : "New slide"}</DialogTitle></DialogHeader>
          {editing && <SlideForm slide={editing} onSaved={() => { setEditing(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
